import { X } from "lucide-react";
import { Button, EmptyState, Icon, Spinner } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import {
  ANSWERABLE_KINDS,
  useAnswerFriendRequest,
  useAnswerOpportunityInvite,
  useAnswerShowShare,
  useAnswerSeatInvite,
  type NotificationAnswer,
} from "../../app/hooks/useNotificationActions.js";
import type { NotificationRow } from "../../app/hooks/useNotifications.js";
import { notificationKindLabel } from "./notificationKind.js";
import { detailsContextFor } from "./NotificationDetails.js";
import { useNotificationDetailsPanel } from "../../app/notificationDetailsPanel.js";
import styles from "./NotificationList.module.scss";

export interface NotificationListProps {
  items: NotificationRow[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  /** Tap a row → mark read + deep-link (the caller owns the sanitized navigation). */
  onOpen: (row: NotificationRow) => void;
  /** Dismiss (soft-delete) a row. */
  onDismiss: (id: string) => void;
  /** Cursor pagination (full-page only): a Load-more button when there are older rows. */
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
}

/**
 * The shared notification-center list body — rows `{kind,title,body,route,read}`
 * with the unread dot, a category label, an absolute device-local timestamp, and per-row dismiss. The
 * per-kind icon/thumbnail was removed at the SME's request (ART-79); the dot now sits alone in the row's
 * own left gutter, so a read row and an unread one share the same text position. Ships the
 * designed empty / loading / error states, the server error surfaced verbatim. Presentational:
 * the drawer (desktop) and the full page (mobile) supply the chrome + the mark-all/clear-all header and own
 * the sanitized navigation; this component only renders the list.
 */
export function NotificationList({
  items,
  isLoading,
  isError,
  error,
  onRetry,
  onOpen,
  onDismiss,
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
}: NotificationListProps) {
  if (isLoading) {
    return (
      <div className={styles.state} role="status" aria-live="polite">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.state}>
        <EmptyState
          title="Couldn't load notifications"
          description={errorMessage(error, "Something went wrong.")}
          action={
            <Button variant="ghost" size="sm" onClick={onRetry}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={styles.state}>
        <EmptyState title="No notifications yet" description="You're all caught up." />
      </div>
    );
  }

  return (
    <>
      <ul className={styles.list}>
        {items.map((n, i) => {
          const kindLabel = notificationKindLabel(n.kind);
          return (
            <li key={n.id ?? `row-${i}`} className={n.read ? styles.row : `${styles.row} ${styles.unread}`}>
              <button
                type="button"
                className={styles.rowMain}
                onClick={() => onOpen(n)}
                aria-label={n.title ?? kindLabel}
              >
                {!n.read && <span className={styles.dot} aria-hidden="true" />}
                <span className={styles.rowText}>
                  <span className={styles.rowTitle}>{n.title}</span>
                  {n.body && <span className={styles.rowBody}>{n.body}</span>}
                  {/* The grey line leads with WHOSE notification this is — the band, or the org an org admin
                      heard it as (PO, 2026-10-05) — for everyone, the same account a push leads its title with.
                      Because the account is shown here, no sentence names it again. */}
                  <span className={styles.rowMeta}>
                    {n.accountName ? `${n.accountName} · ` : ""}
                    {kindLabel} · {formatWhen(n.createdOn)}
                  </span>
                </span>
              </button>
              <button
                type="button"
                className={styles.dismissButton}
                aria-label="Dismiss"
                onClick={() => n.id && onDismiss(n.id)}
              >
                <Icon icon={X} size={16} />
              </button>
              <RowAnswer row={n} onDismiss={onDismiss} />
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <div className={styles.loadMore}>
          <Button variant="ghost" size="sm" onClick={() => onLoadMore?.()} disabled={isFetchingMore}>
            {isFetchingMore ? "Loading…" : "Load older"}
          </Button>
        </div>
      )}
    </>
  );
}

/**
 * INLINE ACCEPT / DECLINE on the rows that are a QUESTION (ART-159, C-Bell) — a friend request, an
 * opportunity invite. Everything else is a statement and gets none.
 *
 * **The endpoints are unchanged.** This is a second door onto the two respond routes, not a second
 * authority: the same calls Contacts and the opportunity card already make, reachable from the place the
 * question arrives. Answering used to mean following the row to a page and finding the item again.
 *
 * **It renders only with BOTH a band and a subject.** The row names the band it is about and the thing it
 * is about; either missing means there is nothing to call, so the row stays a link. That is also what keeps
 * an older payload — served before `subjectId` existed — rendering exactly as it did.
 *
 * **Its own component, and that is load-bearing rather than tidy.** Two mutation hooks mount here; a
 * notification list that raised them at the top would run them for every row on the page, and this list is
 * also the mobile full-page one. Neither hook touches band context (see `useNotificationActions`), because
 * the bell opens from the topbar where there is none to touch.
 */
function RowAnswer({
  row,
  onDismiss,
}: {
  row: NotificationRow;
  onDismiss: (id: string) => void;
}) {
  const showDetails = useNotificationDetailsPanel((s) => s.show);
  const friend = useAnswerFriendRequest();
  const invite = useAnswerOpportunityInvite();
  const share = useAnswerShowShare();
  const seat = useAnswerSeatInvite();

  // ART-327 — a show request (to Mgmt / the artist AV) is answered in its own Details.
  if (row.kind === "show_request" && row.subjectId && detailsContextFor(row)) {
    return (
      <div className={styles.rowActions}>
        <Button size="sm" onClick={() => showDetails({ row, kind: "request", offerId: row.subjectId! })}>
          Details
        </Button>
      </div>
    );
  }
  const target = ANSWERABLE_KINDS[row.kind ?? ""];
  const bandId = row.bandId ?? undefined;
  const subjectId = row.subjectId ?? undefined;
  const parentId = row.subjectParentId ?? undefined;
  // A seat invite is answered person-scoped and needs no band (an org-team invite has none); every other kind does.
  if (!target || !subjectId || (target !== "seat" && !bandId)) return null;
  // A SHARED-DATE row needs both ids, because its respond route is nested. Missing either and the row stays
  // a link — the same rule as a missing subject, for the same reason: there is nothing to call.
  if (target === "share" && !parentId) return null;

  // ART-328 (product owner, R6): an opportunity invite is answered in its Details — the bell opens them rather
  // than answering inline. Only where this band can be opened from here (see `detailsContextFor`); otherwise the
  // row keeps its inline answer.
  if (target === "opportunity" && detailsContextFor(row)) {
    return (
      <div className={styles.rowActions}>
        <Button size="sm" onClick={() => showDetails({ row, kind: "opportunity", id: subjectId })}>
          Details
        </Button>
      </div>
    );
  }

  const pending = friend.isPending || invite.isPending || share.isPending || seat.isPending;
  const send = (answer: NotificationAnswer) => {
    const done = {
      // DISMISSED on success, so the question leaves the list it was asked in. The server clears the
      // friend request for every AV member who got it a beat later; this is the local half of the same
      // outcome, and without it the row you just answered sits there looking unanswered.
      onSuccess: () => { if (row.id) onDismiss(row.id); },
    };
    if (target === "seat") seat.mutate({ inviteId: subjectId, answer }, done);
    else if (target === "friend") friend.mutate({ bandId: bandId as string, friendshipId: subjectId, answer }, done);
    else if (target === "share") {
      share.mutate({ bandId: bandId as string, showId: parentId as string, shareId: subjectId, answer }, done);
    } else invite.mutate({ bandId: bandId as string, oppId: subjectId, answer }, done);
  };

  return (
    <div className={styles.rowActions}>
      <Button size="sm" disabled={pending} onClick={() => send("accept")}>
        Accept
      </Button>
      <Button variant="ghost" size="sm" disabled={pending} onClick={() => send("decline")}>
        Decline
      </Button>
    </div>
  );
}

/** A compact absolute date, device-local 12-hour — never raw UTC / trailing `Z`. No relative-time lib.*/
function formatWhen(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
